interface InheritedPropertyFinalCTAProps {
  formUrl: string;
}

export default function InheritedPropertyFinalCTA({ formUrl }: InheritedPropertyFinalCTAProps) {
  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-900 to-gray-800">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Move Forward with Your Inherited Property?
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Let's discuss your situation and explore your options. No pressure, no obligations—just honest guidance from experienced professionals who understand inherited properties.
            </p>

            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-500 rounded-lg flex-shrink-0">
                  <i className="ri-time-line text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Fast Response</h3>
                  <p className="text-white/80">We'll review your information and respond within 24 hours with initial guidance.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-500 rounded-lg flex-shrink-0">
                  <i className="ri-hand-coin-line text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Fair Cash Offer</h3>
                  <p className="text-white/80">Receive a transparent, all-cash offer based on current market conditions.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-500 rounded-lg flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Zero Obligation</h3>
                  <p className="text-white/80">No commitment required. Use our offer to inform your decision-making process.</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <p className="text-white/90 leading-relaxed">
                <strong className="text-white">Over 500 families</strong> have trusted us with their inherited properties. We understand the emotional and financial complexity you're facing, and we're here to help you navigate it successfully.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <h3 className="text-3xl font-bold mb-6 text-gray-900">Get Your Free Property Evaluation</h3>
            <form
              id="inherited-apartments-contact-form"
              data-readdy-form
              action={formUrl}
              method="POST"
              className="space-y-6"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);
                const data = new URLSearchParams();
                
                formData.forEach((value, key) => {
                  data.append(key, value.toString());
                });

                fetch(formUrl, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                  body: data.toString()
                })
                  .then(response => {
                    if (response.ok) {
                      alert('Thank you! We\'ll contact you within 24 hours to discuss your inherited property.');
                      form.reset();
                    } else {
                      alert('There was an error submitting your information. Please try again or call us directly.');
                    }
                  })
                  .catch(() => {
                    alert('There was an error submitting your information. Please try again or call us directly.');
                  });
              }}
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="propertyAddress" className="block text-sm font-semibold text-gray-700 mb-2">
                  Property Address *
                </label>
                <input
                  type="text"
                  id="propertyAddress"
                  name="propertyAddress"
                  required
                  placeholder="Street, City, State, ZIP"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="units" className="block text-sm font-semibold text-gray-700 mb-2">
                  Number of Units
                </label>
                <input
                  type="number"
                  id="units"
                  name="units"
                  placeholder="e.g., 8"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="probateStatus" className="block text-sm font-semibold text-gray-700 mb-2">
                  Probate Status *
                </label>
                <select
                  id="probateStatus"
                  name="probateStatus"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="">Select status...</option>
                  <option value="not-started">Probate Not Started</option>
                  <option value="in-progress">Probate In Progress</option>
                  <option value="complete">Probate Complete</option>
                  <option value="not-sure">Not Sure</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Tell Us About Your Situation
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="Number of heirs, property condition, timeline needs, etc."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
              </div>

              <button
                type="submit"
                className="w-full whitespace-nowrap bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-600 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer"
              >
                Get My Free Evaluation
              </button>

              <p className="text-xs text-gray-500 text-center">
                By submitting this form, you agree to be contacted about your property. We respect your privacy and will never share your information.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
